import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { Media } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { formatBytes, MediaPreviewDialog, useUpdateMedia } from '@/features/media';
import { MAX_MEDIA_ALT_LENGTH } from '@/shared/media/limits';

import { productKeys } from '../hooks/product-keys';
import { useProduct } from '../hooks/use-product';
import { useVariants } from '../hooks/use-variants';

import {
  isGroupOn,
  isPartialScope,
  toggleVariantGroup,
  variantGroups,
  type GalleryItem,
} from './product-media-gallery';
import { ProductMediaVariantsDialog } from './product-media-variants-dialog';

/** Search engines read about this much; longer text still saves, up to the backend's limit. */
const ALT_TARGET_LENGTH = 125;

export interface ProductMediaDetailsModalProps {
  /** Null before the product exists, when there are no variants to scope the file to. */
  productId: string | null;
  item: GalleryItem;
  /** The whole gallery in order, for the arrows. */
  siblings: readonly Media[];
  /** The file is first in the gallery. */
  featured: boolean;
  /** May change this product's gallery: which variants, featured, remove. */
  canEdit: boolean;
  /** May change the file itself: its alt text and the picture. */
  canEditFile: boolean;
  /** The file's alt text or picture was saved. */
  onMediaUpdated?: ((media: Media) => void) | undefined;
  /** An arrow moved to another file, after this one's changes were kept. */
  onNavigate: (media: Media) => void;
  onClose: () => void;
  /** Done: the gallery changes, kept in the page's draft for the save bar. */
  onApply: (variantIds: string[], makeFeatured: boolean) => void;
  onRemove: () => void;
}

function VariantChip({
  on,
  disabled,
  onClick,
  children,
}: {
  on: boolean;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'focus-visible:ring-ring/50 rounded-full border px-2.5 py-0.5 text-xs font-semibold outline-none focus-visible:ring-[3px] disabled:cursor-default',
        on
          ? 'bg-primary-tint text-primary border-transparent'
          : 'bg-background text-muted-foreground enabled:hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

/**
 * One gallery file on the preview stage. Alt text and the picture belong to the file in the media
 * library, so they save at once; which variants show the file and whether it is featured belong to
 * this product's gallery, so they stay in the page's draft for the save bar. Moving to another file
 * keeps what was changed on this one, as Done does.
 */
export function ProductMediaDetailsModal({
  productId,
  item,
  siblings,
  featured,
  canEdit,
  canEditFile,
  onMediaUpdated,
  onNavigate,
  onClose,
  onApply,
  onRemove,
}: ProductMediaDetailsModalProps) {
  const { t } = useTranslation(['products', 'common']);
  const altId = useId();
  const variantsLabelId = useId();
  const featuredId = useId();
  const invalidates = productId ? [productKeys.media(productId)] : [];
  const update = useUpdateMedia({ invalidates });
  const product = useProduct(productId).data;
  const variants = useVariants(productId).data ?? [];

  // An edited picture shows at once; the gallery's own copy arrives with its refetch.
  const [changed, setChanged] = useState<Media | null>(null);
  const media =
    changed?.id === item.media.id && changed.updatedAt >= item.media.updatedAt
      ? changed
      : item.media;

  const [alt, setAlt] = useState(media.alt ?? '');
  const [scope, setScope] = useState<string[]>(item.variantIds);
  const [makeFeatured, setMakeFeatured] = useState(false);
  const [pickingExact, setPickingExact] = useState(false);
  // The dialog stays open across files, so its fields start over when the file changes.
  const [shownId, setShownId] = useState(item.mediaId);
  if (shownId !== item.mediaId) {
    setShownId(item.mediaId);
    setAlt(media.alt ?? '');
    setScope(item.variantIds);
    setMakeFeatured(false);
    setPickingExact(false);
  }

  const option = [...(product?.options ?? [])].sort((a, b) => a.position - b.position)[0];
  const groups = variantGroups(option, variants);
  const partial = isPartialScope(scope, groups);

  const facts = [
    media.filename ?? t('products:media.untitled'),
    media.width && media.height ? `${media.width} × ${media.height}` : null,
    formatBytes(media.bytes),
  ].filter(Boolean);

  const nextAlt = alt.trim();
  const altChanged = canEditFile && nextAlt !== (media.alt ?? '');
  const altBody = { id: media.id, body: { alt: nextAlt || null } };

  const finish = () => {
    if (canEdit) onApply(scope, makeFeatured);
    onClose();
  };
  const done = () => {
    if (altChanged) {
      update.mutate(altBody, {
        onSuccess: (updated) => {
          onMediaUpdated?.(updated);
          finish();
        },
      });
    } else finish();
  };
  // Doesn't wait for the alt text: a failed save says so in its own toast.
  const navigate = (to: Media) => {
    if (canEdit) onApply(scope, makeFeatured);
    if (altChanged) {
      update
        .mutateAsync(altBody)
        .then((updated) => onMediaUpdated?.(updated))
        .catch(() => undefined);
    }
    onNavigate(to);
  };
  const imageChanged = (updated: Media) => {
    setChanged(updated);
    onMediaUpdated?.(updated);
  };

  const canSave = canEdit || canEditFile;
  return (
    <MediaPreviewDialog
      media={media}
      siblings={siblings}
      onNavigate={navigate}
      title={
        media.kind === 'image'
          ? t('products:mediaDetails.title')
          : t('products:mediaDetails.fileTitle')
      }
      description={facts.join(' · ')}
      canEditImage={canEditFile}
      invalidates={invalidates}
      onImageChanged={imageChanged}
      onClose={onClose}
      footer={
        <>
          {canEdit ? (
            <Button
              type="button"
              variant="link"
              className="text-destructive mr-auto h-auto p-0"
              onClick={onRemove}
            >
              {t('products:media.remove')}
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={onClose}>
            {canSave ? t('common:actions.cancel') : t('common:actions.close')}
          </Button>
          {canSave ? (
            <Button type="button" disabled={update.isPending} onClick={done}>
              {t('common:actions.done')}
            </Button>
          ) : null}
        </>
      }
      panel={
        <div className="flex min-w-0 flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={altId}>{t('products:mediaDetails.alt')}</Label>
            <Textarea
              id={altId}
              rows={3}
              value={alt}
              maxLength={MAX_MEDIA_ALT_LENGTH}
              disabled={!canEditFile}
              onChange={(e) => setAlt(e.target.value)}
            />
            <p className="text-muted-foreground flex justify-between gap-2 text-xs">
              <span>{t('products:mediaDetails.altHint')}</span>
              <span
                className={cn(
                  'whitespace-nowrap tabular-nums',
                  alt.length > ALT_TARGET_LENGTH && 'text-destructive',
                )}
              >
                {t('products:mediaDetails.altCounter', {
                  length: alt.length,
                  max: ALT_TARGET_LENGTH,
                })}
              </span>
            </p>
          </div>

          {productId ? (
            <div className="flex flex-col gap-2.5">
              <div>
                <h3 id={variantsLabelId} className="text-sm font-semibold">
                  {t('products:mediaDetails.variants')}
                </h3>
                {option ? (
                  <p className="text-muted-foreground mt-0.5 text-[13px]">
                    {t('products:mediaDetails.variantsHint', { option: option.name.toLowerCase() })}
                  </p>
                ) : null}
              </div>
              <div
                role="group"
                aria-labelledby={variantsLabelId}
                className="flex flex-wrap gap-1.5"
              >
                <VariantChip
                  on={scope.length === 0}
                  disabled={!canEdit}
                  onClick={() => setScope([])}
                >
                  {t('products:mediaDetails.allVariants')}
                </VariantChip>
                {groups.map((group) => (
                  <VariantChip
                    key={group.value}
                    on={scope.length > 0 && isGroupOn(scope, group)}
                    disabled={!canEdit}
                    onClick={() => setScope(toggleVariantGroup(scope, group, variants.length))}
                  >
                    {group.label}
                  </VariantChip>
                ))}
              </div>
              {partial ? (
                <p className="text-muted-foreground text-xs">
                  {t('products:mediaDetails.partialScope', { count: scope.length })}
                </p>
              ) : null}
              {canEdit && variants.length > 1 ? (
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  className="text-primary h-auto self-start p-0 text-[13px]"
                  onClick={() => setPickingExact(true)}
                >
                  {t('products:mediaDetails.pickExact')}
                </Button>
              ) : null}
            </div>
          ) : null}

          <div className="flex items-start gap-3">
            <Switch
              id={featuredId}
              className="mt-0.5"
              checked={featured || makeFeatured}
              disabled={!canEdit || featured}
              onCheckedChange={setMakeFeatured}
            />
            <div className="flex flex-col gap-0.5">
              <Label htmlFor={featuredId}>{t('products:mediaDetails.featured')}</Label>
              <p className="text-muted-foreground text-xs">
                {t('products:mediaDetails.featuredHint')}
              </p>
            </div>
          </div>
          {pickingExact ? (
            <ProductMediaVariantsDialog
              fileCount={1}
              initial={scope}
              mixed={false}
              variants={variants}
              onClose={() => setPickingExact(false)}
              onSave={setScope}
            />
          ) : null}
        </div>
      }
    />
  );
}
